import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Calculator, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../services/api";
import { formatInr } from "./payroll/runPayrollUtils";

function isoToDdMmYyyy(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function ddMmYyyyToIso(value) {
  const s = String(value || "").trim();
  const dmy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return "";
}

/**
 * Quarter-wise Port Data — deduction / salary paid dates, interest & penalty.
 * Step 1 only. Mirrors legacy TDS utility port-data flow for the selected quarter.
 */
export default function Form24QPortDataModal({
  open,
  onClose,
  darkMode = false,
  financialYear,
  quarter,
  filingId = null,
  initialPortData = null,
  onPorted,
}) {
  const [loading, setLoading] = useState(false);
  const [porting, setPorting] = useState(false);
  const [months, setMonths] = useState([]);
  const [excludeZeroTds, setExcludeZeroTds] = useState(true);
  const [consolidatedDeductions, setConsolidatedDeductions] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [empFilter, setEmpFilter] = useState("");
  const [showEmpTable, setShowEmpTable] = useState(false);

  const inputCls = `mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
    darkMode ? "border-[#555] bg-[#2a2a2a] text-white" : "border-gray-300 bg-white"
  }`;

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await payrollOpsService.getForm24QPortPreview({
        financialYear,
        quarter,
        excludeZeroTds,
      });
      const data = res?.data || res;
      let nextMonths = data.months || [];
      if (initialPortData?.months?.length) {
        nextMonths = initialPortData.months;
        setExcludeZeroTds(initialPortData.excludeZeroTds !== false);
        setConsolidatedDeductions(!!initialPortData.consolidatedDeductions);
        setAcknowledged(!!initialPortData.acknowledged);
        setCalcResult({
          interestPenalty: initialPortData.interestPenalty,
          q4Validation: initialPortData.q4Validation,
        });
      }
      setMonths(nextMonths);
      if (!initialPortData?.months?.length) {
        await runCalculateWithMonths(nextMonths, false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load port preview");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      setCalcResult(null);
      setAcknowledged(!!initialPortData?.acknowledged);
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, financialYear, quarter]);

  const updateMonth = (payMonth, patch) => {
    setMonths((prev) =>
      prev.map((m) => (m.payMonth === payMonth ? { ...m, ...patch } : m))
    );
    setCalcResult(null);
    setAcknowledged(false);
  };

  const applyDefaultDeductionDates = () => {
    setMonths((prev) =>
      prev.map((m) => ({
        ...m,
        deductionDate: m.defaultDeductionDate || m.deductionDate,
      }))
    );
    setCalcResult(null);
  };

  const clearDeductionDates = () => {
    setMonths((prev) => prev.map((m) => ({ ...m, deductionDate: "" })));
    setCalcResult(null);
  };

  const applyDefaultPaymentDates = () => {
    setMonths((prev) =>
      prev.map((m) => ({
        ...m,
        salaryPaidDate: m.defaultSalaryPaidDate || m.salaryPaidDate,
      }))
    );
    setCalcResult(null);
  };

  const clearPaymentDates = () => {
    setMonths((prev) => prev.map((m) => ({ ...m, salaryPaidDate: "" })));
    setCalcResult(null);
  };

  const handleExcludeZeroChange = (checked) => {
    setExcludeZeroTds(checked);
    setMonths((prev) =>
      prev.map((m) => ({
        ...m,
        enabled: checked ? Number(m.tdsAmount || 0) > 0.01 : true,
      }))
    );
    setCalcResult(null);
  };

  const runCalculateWithMonths = async (monthRows, ack = acknowledged) => {
    setPorting(true);
    try {
      const payload = {
        financialYear,
        quarter,
        filingId,
        months: (monthRows || months).map((m) => ({
          ...m,
          deductionDate: ddMmYyyyToIso(m.deductionDate) || m.deductionDate,
          salaryPaidDate: ddMmYyyyToIso(m.salaryPaidDate) || m.salaryPaidDate,
        })),
        excludeZeroTds,
        consolidatedDeductions,
        acknowledged: ack,
      };
      const res = await payrollOpsService.calculateForm24QPortData(payload);
      const data = res?.data || res;
      setCalcResult(data);
      if (data.monthErrors?.length) {
        toast.error(data.monthErrors[0].message);
        return null;
      }
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.message || "Calculation failed");
      return null;
    } finally {
      setPorting(false);
    }
  };

  const runCalculate = async (ack = acknowledged) => runCalculateWithMonths(months, ack);

  const handlePort = async () => {
    const calc = await runCalculate(acknowledged);
    if (!calc) return;

    if (calc.interestPenalty?.requiresPayment && !acknowledged) {
      toast.error("Acknowledge interest / penalty payment before porting");
      return;
    }
    if (quarter === "Q4" && calc.q4Validation && !calc.q4Validation.passed) {
      toast.error("Resolve Q4 salary vs TDS mismatches before porting");
      setShowEmpTable(true);
      return;
    }

    const portData = {
      financialYear,
      quarter,
      months: months.map((m) => ({
        ...m,
        deductionDate: ddMmYyyyToIso(m.deductionDate) || m.deductionDate,
        salaryPaidDate: ddMmYyyyToIso(m.salaryPaidDate) || m.salaryPaidDate,
      })),
      excludeZeroTds,
      consolidatedDeductions,
      acknowledged,
      interestPenalty: calc.interestPenalty,
      q4Validation: calc.q4Validation,
      canProceed:
        calc.canProceed ||
        (!calc.interestPenalty?.requiresPayment && calc.q4Validation?.passed !== false),
      portedAt: new Date().toISOString(),
    };

    if (filingId) {
      try {
        await payrollOpsService.saveForm24QPortData({ ...portData, filingId });
      } catch (e) {
        toast.error(e?.response?.data?.message || "Save failed");
        return;
      }
    }

    toast.success("Port Data saved for this quarter");
    onPorted?.(portData);
    onClose(true);
  };

  const interest = calcResult?.interestPenalty;
  const q4 = calcResult?.q4Validation;

  const filteredEmployees = useMemo(() => {
    const rows = q4?.employeeRows || [];
    if (!empFilter.trim()) return rows;
    const s = empFilter.toLowerCase();
    return rows.filter(
      (r) =>
        String(r.name || "").toLowerCase().includes(s) ||
        String(r.empCode || "").toLowerCase().includes(s)
    );
  }, [q4, empFilter]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/50 p-4"
      onClick={() => onClose(false)}
    >
      <div
        className={`flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl shadow-xl ${
          darkMode ? "bg-[#1e1e1e] text-white" : "bg-white"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Port Data"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-base font-bold">Port Data — {quarter}</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {financialYear} · Quarter months only · Default dates = last calendar day of month
            </p>
          </div>
          <button type="button" onClick={() => onClose(false)} className="rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-[#333]">
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="animate-spin text-gray-400" size={28} />
            </div>
          ) : (
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="min-w-0 flex-1">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]">
                    <tr>
                      <th className="px-2 py-2 w-8" />
                      <th className="px-2 py-2">Month</th>
                      <th className="px-2 py-2">Deduction date</th>
                      <th className="px-2 py-2">Salary paid date</th>
                      <th className="px-2 py-2 text-right">TDS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m) => (
                      <tr
                        key={m.payMonth}
                        className={`border-t ${darkMode ? "border-[#333]" : "border-gray-100"} ${
                          !m.enabled ? "opacity-50" : ""
                        }`}
                      >
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={!!m.enabled}
                            onChange={(e) => updateMonth(m.payMonth, { enabled: e.target.checked })}
                          />
                        </td>
                        <td className="px-2 py-1.5 font-medium">{m.label}</td>
                        <td className="px-2 py-1.5">
                          <input
                            type="text"
                            placeholder="dd/mm/yyyy"
                            value={isoToDdMmYyyy(m.deductionDate)}
                            onChange={(e) =>
                              updateMonth(m.payMonth, { deductionDate: e.target.value })
                            }
                            className={`${inputCls} !mt-0 w-28 font-mono`}
                            disabled={!m.enabled}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="text"
                            placeholder="dd/mm/yyyy"
                            value={isoToDdMmYyyy(m.salaryPaidDate)}
                            onChange={(e) =>
                              updateMonth(m.payMonth, { salaryPaidDate: e.target.value })
                            }
                            className={`${inputCls} !mt-0 w-28 font-mono`}
                            disabled={!m.enabled}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {formatInr(m.tdsAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-4 space-y-2">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={excludeZeroTds}
                      onChange={(e) => handleExcludeZeroChange(e.target.checked)}
                    />
                    Do not port those employees having TDS amount zero
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={consolidatedDeductions}
                      onChange={(e) => setConsolidatedDeductions(e.target.checked)}
                    />
                    Consolidated deductions for salary and one-time tax deductions
                  </label>
                </div>

                <div className="mt-4 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                  <p className="font-semibold text-[hsl(var(--ds-fg))]">How interest / penalty is calculated</p>
                  <ul className="mt-2 list-disc space-y-1 pl-4">
                    <li>
                      <strong>Late deduction (u/s 201(1A)):</strong> 1% per month on TDS when deduction date is after salary paid date.
                    </li>
                    <li>
                      <strong>Late deposit:</strong> 1.5% per month on TDS when challan deposit is after the 7th of the next month.
                    </li>
                    <li>
                      <strong>Late filing (u/s 234E):</strong> ₹200 per day (max = quarter TDS) when return is filed after the statutory due date.
                    </li>
                    <li>
                      Default deduction and salary paid dates are the <strong>last calendar day</strong> of each month (Apr 30, May 31, Jun 30, etc.).
                    </li>
                  </ul>
                </div>

                {interest && (
                  <div
                    className={`mt-4 rounded-lg border p-3 text-xs ${
                      interest.requiresPayment
                        ? "border-amber-200 bg-amber-50 text-amber-900"
                        : "border-emerald-200 bg-emerald-50 text-emerald-900"
                    }`}
                  >
                    <p className="font-semibold flex items-center gap-1">
                      {interest.requiresPayment ? (
                        <>
                          <AlertTriangle size={14} /> Interest / penalty payable before filing
                        </>
                      ) : (
                        <>No interest / penalty for current dates and challans</>
                      )}
                    </p>
                    {(interest.details || []).length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {(interest.details || []).map((d, i) => (
                          <li key={i}>
                            • {d.message} — {formatInr(d.amount)}
                            {d.rate ? ` (${d.rate}, ${d.months} month(s))` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                    {(interest.notices || []).length > 0 && (
                      <ul className="mt-2 space-y-1 text-[hsl(var(--ds-muted-fg))]">
                        {(interest.notices || []).map((n, i) => (
                          <li key={i}>• {n.message}</li>
                        ))}
                      </ul>
                    )}
                    {interest.requiresPayment && (
                      <>
                        <p className="mt-2 font-bold">
                          Total: {formatInr(interest.totalPayable)} (Interest {formatInr(interest.totalInterest)} + Penalty {formatInr(interest.totalPenalty)})
                        </p>
                        <p className="mt-1">
                          Pay this via a separate challan (interest / 234E fee fields) in Step 3 before filing.
                        </p>
                        <label className="mt-2 flex items-center gap-2 font-medium">
                          <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                          />
                          I confirm interest / penalty will be paid via challan before proceeding
                        </label>
                      </>
                    )}
                  </div>
                )}

                {quarter === "Q4" && q4 && !q4.passed && (
                  <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                    <p className="font-semibold">Q4 — salary vs TDS mismatch (employee-wise)</p>
                    <ul className="mt-1 space-y-1">
                      {(q4.mismatches || []).slice(0, 5).map((m, i) => (
                        <li key={i}>• {m.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <aside className="w-full shrink-0 space-y-2 lg:w-52">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Date actions
                </p>
                {[
                  ["Apply default deduction date", applyDefaultDeductionDates],
                  ["Clear all deduction date", clearDeductionDates],
                  ["Apply default payment date", applyDefaultPaymentDates],
                  ["Clear all payment date", clearPaymentDates],
                ].map(([label, fn]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={fn}
                    className={`w-full rounded-lg border px-2 py-2 text-left text-[11px] font-medium ${
                      darkMode ? "border-[#555] hover:bg-[#2a2a2a]" : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => runCalculate(false)}
                  disabled={porting}
                  className={`mt-2 flex w-full items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-semibold ${
                    darkMode ? "border-[#555]" : "border-gray-300"
                  }`}
                >
                  {porting ? <Loader2 size={12} className="animate-spin" /> : <Calculator size={12} />}
                  Calculate interest
                </button>
              </aside>
            </div>
          )}

          {quarter === "Q4" && showEmpTable && filteredEmployees.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold">Q4 employee salary vs TDS</p>
                <input
                  value={empFilter}
                  onChange={(e) => setEmpFilter(e.target.value)}
                  placeholder="Filter employee"
                  className={`${inputCls} !mt-0 w-40 text-xs`}
                />
              </div>
              <div className="max-h-40 overflow-auto">
                <table className="w-full text-left text-[11px]">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="px-2 py-1">Code</th>
                      <th className="px-2 py-1">Name</th>
                      <th className="px-2 py-1 text-right">Salary</th>
                      <th className="px-2 py-1 text-right">TDS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((e) => (
                      <tr key={e.employeeId} className="border-t">
                        <td className="px-2 py-1 font-mono">{e.empCode}</td>
                        <td className="px-2 py-1">{e.name}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatInr(e.salaryTotal)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatInr(e.tdsTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div
          className={`flex flex-wrap items-center gap-2 border-t px-5 py-3 ${
            darkMode ? "border-[#333] bg-[#242424]" : "border-gray-100 bg-gray-50"
          }`}
        >
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={showEmpTable} onChange={(e) => setShowEmpTable(e.target.checked)} />
            Filter employee {quarter === "Q4" ? "(Q4 match)" : ""}
          </label>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => onClose(false)}
              className={`rounded-lg border px-4 py-2 text-sm ${
                darkMode ? "border-[#555]" : "border-gray-300"
              }`}
            >
              Close
            </button>
            <button
              type="button"
              onClick={handlePort}
              disabled={porting || loading}
              className="rounded-lg bg-redhalo-red px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {porting ? <Loader2 size={14} className="inline animate-spin" /> : null} Port
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
